import { useAuth, useOrganization } from '@clerk/clerk-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AGENT_URL } from '@/config/env'
import {
  ApiError,
  fetchJsonWithAuth,
  mutateJsonWithAuth,
  requireAuthToken,
} from '@/lib/api'
import { organizationMappings } from './model'
import type {
  FieldSource,
  SigningConfiguration,
  SigningField,
  SigningForm,
} from './model'

export interface CatalogForm {
  revision?: string
  key: string
  title: string
  org_slug: string
  enabled: boolean
  owner_enabled: boolean
  org_enabled: boolean | null
  readiness: 'ready' | 'needs_review'
  last_updated_at: string
  annotations: {
    fields?: Record<
      string,
      {
        type: SigningField['type']
        description: string
        required: boolean
        ownership?: SigningField['ownership']
        source?: FieldSource | null
      }
    >
  }
  inputs_schema: { properties: Record<string, { type: string }> }
}

export interface PrefillSummary {
  id: string
  form_key: string
  name: string
}

export interface OrganizationSigningSettings {
  enabled: boolean
}
export interface Prefill extends PrefillSummary {
  mappings: SigningConfiguration['mappings']
  updated_at: string
}

export function configurationFromApi(prefill: Prefill): SigningConfiguration {
  return {
    id: prefill.id,
    formKey: prefill.form_key,
    name: prefill.name,
    mappings: prefill.mappings,
    updatedAt: prefill.updated_at,
  }
}

export function formFromApi(form: CatalogForm): SigningForm {
  return {
    key: form.key,
    name: form.key.toUpperCase(),
    revision: form.revision,
    description: '',
    updatedAt: form.last_updated_at,
    fields: Object.entries(form.annotations.fields ?? {}).flatMap(
      ([name, policy]) => {
        const property = form.inputs_schema.properties[name]
        if (
          !property ||
          property.type !== (policy.type === 'checkbox' ? 'boolean' : 'string')
        )
          return []
        return [
          {
            name,
            label: policy.description || name,
            group: '',
            type: policy.type,
            required: policy.required,
            ownership: policy.ownership ?? 'organization',
            source: policy.source ?? undefined,
            page: -1,
            rect: [0, 0, 0, 0],
          } as SigningField,
        ]
      }
    ),
  }
}

export function useSigningConfigurationQuery<T>(
  path: string,
  enabled = true,
  watch: boolean | number | ((data: T | undefined) => number | false) = false
) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  return useQuery({
    queryKey: ['signing-configuration', organization?.id, path],
    enabled: enabled && Boolean(organization?.id),
    queryFn: async ({ signal }) =>
      fetchJsonWithAuth<T>({
        token: await requireAuthToken(() =>
          getToken({ organizationId: organization?.id })
        ),
        signal,
        url: `${AGENT_URL}/signing/${path}`,
        errorMessage: 'Could not load document configuration.',
      }),
    // Open editors follow saved changes from David or another workspace.
    // useEditorDraft retains local edits until Cancel or a successful Save.
    refetchInterval:
      typeof watch === 'function'
        ? (query) => watch(query.state.data)
        : typeof watch === 'number'
          ? watch
          : watch
            ? 5_000
            : false,
    refetchOnWindowFocus: Boolean(watch),
  })
}

export function formPolicyPatch(form: SigningForm) {
  if (!form.updatedAt) throw new Error('Reload this document before saving.')
  return {
    expected_updated_at: form.updatedAt,
    fields: Object.fromEntries(
      form.fields.map((field) => [
        field.name,
        {
          required: field.required,
          ownership: field.ownership,
          source: field.ownership === 'template' ? field.source : null,
        },
      ])
    ),
  }
}

export function configurationPatch(
  form: SigningForm,
  configuration: SigningConfiguration,
  saved: SigningConfiguration
) {
  if (!configuration.updatedAt)
    throw new Error('Reload this configuration before saving.')
  const mappings = organizationMappings(form, configuration.mappings)
  // Explicit null deletes cleared or newly managed mappings. Missing keys in
  // Relay PATCH leave old values intact; blank strings and false are values.
  return {
    name: configuration.name.trim(),
    expected_updated_at: configuration.updatedAt,
    mappings: Object.fromEntries(
      [
        ...new Set([
          ...Object.keys(saved.mappings),
          ...Object.keys(configuration.mappings),
        ]),
      ].map((name) => [name, mappings[name] ?? null])
    ),
  }
}

export function useSigningPersistence() {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const cache = useQueryClient()
  const orgId = organization?.id
  async function write<T>(
    path: string,
    body: unknown,
    method: 'POST' | 'PATCH'
  ): Promise<T> {
    if (!orgId) throw new Error('Choose an organization before saving.')
    let result: T
    try {
      result = await mutateJsonWithAuth<T>({
        token: await requireAuthToken(() =>
          getToken({ organizationId: orgId })
        ),
        url: `${AGENT_URL}/signing/${path}`,
        method,
        body,
        errorMessage: 'Could not save document configuration.',
      })
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        await cache.invalidateQueries({
          queryKey: ['signing-configuration', orgId],
        })
        throw new Error(
          'This configuration changed elsewhere. Your edits are still here; Cancel loads the latest saved version.',
          { cause: error }
        )
      }
      throw error
    }
    if (path !== 'prefills' && !path.endsWith('/assets'))
      cache.setQueryData(
        ['signing-configuration', orgId, path.replace(/\/policies$/, '')],
        result
      )
    void cache.invalidateQueries({ queryKey: ['signing-configuration', orgId] })
    void cache.invalidateQueries({ queryKey: ['signing-facts', orgId] })
    return result
  }
  return {
    retryPreview: (formKey: string) =>
      write(`forms/${encodeURIComponent(formKey)}/assets`, {}, 'POST'),
    setOrganizationEnabled: (enabled: boolean) =>
      write<OrganizationSigningSettings>('settings', { enabled }, 'PATCH'),
    saveForm: async (form: SigningForm) =>
      formFromApi(
        await write<CatalogForm>(
          `forms/${encodeURIComponent(form.key)}/policies`,
          formPolicyPatch(form),
          'PATCH'
        )
      ),
    saveConfiguration: async (
      form: SigningForm,
      configuration: SigningConfiguration,
      saved: SigningConfiguration
    ) =>
      configurationFromApi(
        await write<Prefill>(
          `prefills/${encodeURIComponent(configuration.id)}`,
          configurationPatch(form, configuration, saved),
          'PATCH'
        )
      ),
    createConfiguration: async (formKey: string, name: string) => {
      const result = await write<Prefill>(
        'prefills',
        { form_key: formKey, name: name.trim(), mappings: {} },
        'POST'
      )
      cache.setQueryData(
        ['signing-configuration', orgId, `prefills/${result.id}`],
        result
      )
      return configurationFromApi(result)
    },
    setFormEnabled: (form: CatalogForm, enabled: boolean) =>
      write<CatalogForm>(
        `forms/${encodeURIComponent(form.key)}`,
        { enabled, expected_updated_at: form.last_updated_at },
        'PATCH'
      ),
  }
}
